import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { apiService } from '@services/api';
import { useSnackbar } from '@components/SnackbarNotification';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiService.forgotPassword(email);
      
      if (response.data.success) {
        setIsSuccess(true);
        showSnackbar(response.data.message, 'success');
      } else {
        setError(getFriendlyErrorMessage({ response: { data: response.data } }, 'Erro ao solicitar reset de senha'));
      }
    } catch (err: unknown) {
      const errorMessage = getFriendlyErrorMessage(err, 'Erro ao solicitar reset de senha');
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    setIsSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Esqueci minha senha</DialogTitle>
      <DialogContent>
        {isSuccess ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Email enviado!
              </Typography>
              <Typography variant="body2">
                Se o email <strong>{email}</strong> estiver cadastrado, você receberá um link para redefinir sua senha.
                Verifique sua caixa de entrada e a pasta de spam.
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              O link expira em 1 hora. Se não receber o email em alguns minutos, verifique se o endereço está correto e tente novamente.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Digite seu email cadastrado e enviaremos um link para redefinir sua senha.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="email"
              disabled={isLoading}
              error={!!error}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {isSuccess ? (
          <Button onClick={handleClose} variant="contained" fullWidth>
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? 'Enviando...' : 'Enviar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

